import { PhaseDto } from "../../../../dtos/PhaseDto";

// Every user-facing string for the resubmission mechanism lives here so the components stay
// consistent. "Cycle" is the internal name and is never shown to a user - it is always a
// submission or a resubmission in the UI.
const startWarning =
    "The current submission keeps its statuses and subtasks as they are; files and comments can still be added to it.";

const priorNotice =
    "This is a previous submission. Files and comments can still be added; its statuses and subtasks are fixed.";

export interface TaskCycleVocabulary {
    // Label for the button that starts a new one.
    startAction: string;
    confirmTitle: string;
    confirmPrompt: (taskName: string) => string;
    // Load panel text while the new one is being created.
    startingMessage: string;
    startedMessage: string;
    // Banner shown when viewing one that is no longer the current one.
    priorMessage: string;
    // Name by number - 0 (or null) is the first one.
    cycleLabel: (cycle: number | null) => string;
    // Wording for the subtask header, naming which one the open subtask belongs to.
    viewingLabel: (cycle: number | null) => string;
}

// The number in the labels matches the stored Resubmission value and the R{n} SharePoint folder,
// so "Resubmission #2" lines up with the data and the folders.
export const cycleVocabulary: TaskCycleVocabulary = {
    startAction: "Start Resubmission",
    confirmTitle: "Start resubmission",
    confirmPrompt: (taskName) => `Start a new resubmission for ${taskName}? ${startWarning}`,
    startingMessage: "Starting resubmission…",
    startedMessage: "Resubmission started.",
    priorMessage: priorNotice,
    cycleLabel: (cycle) => ((cycle ?? 0) === 0 ? "Original submission" : `Resubmission #${cycle}`),
    viewingLabel: (cycle) =>
        (cycle ?? 0) === 0 ? "Viewing original submission" : `Viewing resubmission #${cycle}`
};

// The template opts in per task, mirroring the server-side gate.
export const allowsNewCycle = (phase?: PhaseDto | null): boolean =>
    phase?.defaultPhase?.settings?.allowResubmission === true;

// Cycles newest-first (e.g. [R2, R1, Original]) so the latest is the default.
export const cyclesNewestFirst = (phase?: PhaseDto | null) =>
    [...(phase?.phaseSubmissions ?? [])].sort((a, b) => (b.resubmission ?? 0) - (a.resubmission ?? 0));

// The status to show for the cycle in view. Phase.Status is shared by every cycle and always
// describes the active one, so a closed cycle uses the status derived from its own steps. Falls
// back to the phase's status where that derivation is inconclusive - on Phase/Step tasks a status
// set by a task action is not recoverable from subtasks (ticket 1023).
export const cycleStatusFor = (phase?: PhaseDto | null, selectedSubmissionId?: string | null) => {
    if (!isClosedCycle(phase, selectedSubmissionId)) return phase?.status;

    const cycle = cyclesNewestFirst(phase).find(c => c.id === selectedSubmissionId);
    return cycle?.status ?? phase?.status;
};

// Whether the cycle in view is a prior one. The server refuses status changes and subtask edits on
// it (files and comments stay open) and returns no actions for it, so the UI reads this to stop
// offering those rather than letting the user try and be rejected. Null selection means the newest
// cycle, which is never closed.
export const isClosedCycle = (phase?: PhaseDto | null, selectedSubmissionId?: string | null): boolean => {
    const cycles = cyclesNewestFirst(phase);
    if (cycles.length < 2) return false;

    // No selection means the newest cycle.
    if (!selectedSubmissionId) return false;

    // A cycle just created is selected before the phase reloads with it in the list. Treating an
    // id we cannot place as closed would lock down the brand-new cycle, so only call it closed
    // when it is present and is genuinely not the newest. The server is the real gate either way.
    if (!cycles.some(c => c.id === selectedSubmissionId)) return false;

    return selectedSubmissionId !== cycles[0].id;
};
